import { Button, Checkbox, FormControlLabel, Stack } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { useMemo } from "react";
import type {
  LayerWeightLimits,
  ScrapMixFilterOption,
  ScrapMixFilters,
  ScrapMixFilterType,
} from "../models";
import { CompactMultiSelect } from "./CompactMultiSelect";

interface Props {
  filters: ScrapMixFilters;
  options: ScrapMixFilterOption[];
  layerWeightLimits: LayerWeightLimits;
  isLoading: boolean;
  onChange: (filters: ScrapMixFilters) => void;
  onApply: () => void;
  onClear: () => void;
}

export const filterFields: Array<{
  type: ScrapMixFilterType;
  field:
    | "shifts"
    | "crews"
    | "gradeIds"
    | "recipeFamilyIds"
    | "operatorIds"
    | "chargeNumbers"
    | "heatNames";
  label: string;
  testId: string;
  width: number;
  menuWidth?: number;
  optionNoWrap?: boolean;
}> = [
  {
    type: "Recipe",
    field: "recipeFamilyIds",
    label: "Recipe",
    testId: "recipe-filter",
    width: 150,
    menuWidth: 180,
  },
  {
    type: "ChargeNumber",
    field: "chargeNumbers",
    label: "Charge #",
    testId: "charge-number-filter",
    width: 105,
  },
  {
    type: "Shift",
    field: "shifts",
    label: "Shift",
    testId: "shift-filter",
    width: 85,
  },
  {
    type: "Crew",
    field: "crews",
    label: "Crew",
    testId: "crew-filter",
    width: 85,
  },
  {
    type: "Operator",
    field: "operatorIds",
    label: "Operator",
    testId: "operator-filter",
    width: 145,
    menuWidth: 170,
  },
  {
    type: "Grade",
    field: "gradeIds",
    label: "Grade",
    testId: "grade-filter",
    width: 185,
    menuWidth: 230,
    optionNoWrap: true,
  },
  {
    type: "Heat",
    field: "heatNames",
    label: "Heat",
    testId: "heat-filter",
    width: 125,
    menuWidth: 145,
  },
];

export const AnalysisFilters = ({
  filters,
  options,
  layerWeightLimits,
  isLoading,
  onChange,
  onApply,
  onClear,
}: Props) => {
  const optionsByType = useMemo(() => {
    const grouped = new Map<ScrapMixFilterType, ScrapMixFilterOption[]>();
    for (const option of options) {
      const group = grouped.get(option.FilterType) ?? [];
      group.push(option);
      grouped.set(option.FilterType, group);
    }
    return grouped;
  }, [options]);

  const invalidDateRange = filters.from.isAfter(filters.to, "day");

  return (
    <Stack spacing={1.5}>
      <DatePicker
        label="From"
        format="MM/DD/YYYY"
        value={filters.from}
        onChange={(value) => {
          if (value?.isValid()) onChange({ ...filters, from: value });
        }}
        slotProps={{
          textField: {
            size: "small",
            fullWidth: true,
            error: invalidDateRange,
            inputProps: { "data-testid": "from-filter" },
          },
        }}
      />
      <DatePicker
        label="To"
        format="MM/DD/YYYY"
        value={filters.to}
        onChange={(value) => {
          if (value?.isValid()) onChange({ ...filters, to: value });
        }}
        slotProps={{
          textField: {
            size: "small",
            fullWidth: true,
            error: invalidDateRange,
            helperText: invalidDateRange ? "To must be on or after From" : "",
            inputProps: { "data-testid": "to-filter" },
          },
        }}
      />

      {filterFields.map(
        ({ type, field, label, testId, menuWidth, optionNoWrap }) => (
          <CompactMultiSelect
            key={type}
            label={label}
            options={optionsByType.get(type) ?? []}
            values={filters[field]}
            onChange={(values) => onChange({ ...filters, [field]: values })}
            testId={testId}
            menuWidth={menuWidth}
            optionNoWrap={optionNoWrap}
          />
        ),
      )}

      <FormControlLabel
        control={
          <Checkbox
            checked={filters.excludeOutOfRangeLayers}
            onChange={(event) =>
              onChange({
                ...filters,
                excludeOutOfRangeLayers: event.target.checked,
              })
            }
            size="small"
          />
        }
        label={`Exclude out-of-range layers (${layerWeightLimits.min.toLocaleString()} to ${layerWeightLimits.max.toLocaleString()} lb)`}
        data-testid="exclude-out-of-range-layers-filter"
        sx={{ ml: 0, mr: 0 }}
      />

      <Stack direction="row" spacing={1}>
        <Button
          variant="outlined"
          onClick={onClear}
          disabled={isLoading}
          data-testid="clear-filters"
          fullWidth
        >
          Clear
        </Button>
        <Button
          variant="contained"
          onClick={onApply}
          disabled={isLoading || invalidDateRange}
          data-testid="apply-filters"
          fullWidth
        >
          Apply Filters
        </Button>
      </Stack>
    </Stack>
  );
};
